import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Mailbox, UserRole, UserSummary } from "@websede/contracts";
import { Copy, KeyRound, Mail, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { useSession } from "../../auth/session.js";
import { Avatar } from "../../components/avatar.js";
import { Button, Card, ErrorNotice, Field, Input, Select } from "../../components/form.js";
import { MailboxAliases, MailGroupsCard } from "../../components/mail-addresses.js";
import { InvitationCards } from "../settings/team.js";

export function AdminUsersPage() {
  const { t } = useTranslation();
  return (
    <section className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-4 md:p-8">
      <h1 className="text-[22px] text-ink">{t("admin.nav.users")}</h1>
      <MembersCard />
      <MailboxesCard />
      <MailGroupsCard />
      <InvitationCards />
    </section>
  );
}

const ASSIGNABLE_ROLES: UserRole[] = ["user", "admin", "billing", "group_manager"];

function MembersCard() {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const [open, setOpen] = useState<string | null>(null);
  const users = useQuery({ queryKey: ["users", "list"], queryFn: () => api.users.list() });
  const format = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(iso),
    );
  const me = session.data?.user;

  return (
    <Card title={t("admin.users.members")}>
      <ErrorNotice error={users.error} />
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-rule text-left text-xs text-ink-3">
              <th className="py-2 pr-3 font-medium">{t("admin.users.user")}</th>
              <th className="hidden py-2 pr-3 font-medium sm:table-cell">
                {t("settings.team.role")}
              </th>
              <th className="hidden py-2 pr-3 font-medium md:table-cell">
                {t("admin.users.security")}
              </th>
              <th className="hidden py-2 pr-3 font-medium lg:table-cell">
                {t("admin.users.lastSignIn")}
              </th>
              <th className="py-2 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            {(users.data?.users ?? []).map((u) => (
              <MemberRow
                key={u.id}
                user={u}
                isSelf={u.id === me?.id}
                actorRole={me?.role ?? "user"}
                open={open === u.id}
                onToggle={() => setOpen(open === u.id ? null : u.id)}
                format={format}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Mailboxes on the workspace's domains; the app password is shown once after creation. */
function MailboxesCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ["users", "list"], queryFn: () => api.users.list() });
  const domains = useQuery({ queryKey: ["domains", "owned"], queryFn: () => api.domains.owned() });
  const mailboxes = useQuery({ queryKey: ["mailboxes"], queryFn: () => api.mailboxes.list() });
  const [userId, setUserId] = useState("");
  const [domainId, setDomainId] = useState("");
  const [localPart, setLocalPart] = useState("");
  const [secret, setSecret] = useState<{ address: string; appPassword: string } | null>(null);

  const ready = (domains.data?.domains ?? []).filter((d) => d.mailSetupAt);
  const withoutMailbox = (users.data?.users ?? []).filter(
    (u) => !(mailboxes.data?.mailboxes ?? []).some((m) => m.userId === u.id),
  );
  // The select shows the first ready domain before the user touches it; mirror that in the request.
  const selectedDomain = ready.find((d) => d.id === domainId) ?? ready[0];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
  const create = useMutation({
    mutationFn: () =>
      api.mailboxes.create({ userId, domainId: selectedDomain?.id ?? domainId, localPart }),
    onSuccess: async (result) => {
      setSecret({ address: result.mailbox.address, appPassword: result.appPassword });
      setLocalPart("");
      setUserId("");
      await refresh();
    },
  });
  const reset = useMutation({
    mutationFn: (m: Mailbox) => api.mailboxes.resetPassword(m.id).then((r) => ({ m, r })),
    onSuccess: ({ m, r }) => setSecret({ address: m.address, appPassword: r.appPassword }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.mailboxes.remove(id),
    onSuccess: refresh,
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    create.mutate();
  };
  return (
    <Card title={t("admin.mailboxes.title")}>
      {ready.length === 0 ? (
        <p className="text-sm text-ink-3">{t("admin.mailboxes.noDomain")}</p>
      ) : (
        <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="sm:w-56">
            <Field label={t("admin.users.user")}>
              <Select required value={userId} onChange={(e) => setUserId(e.target.value)}>
                <option value="">—</option>
                {withoutMailbox.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="flex-1">
            <Field label={t("admin.mailboxes.address")}>
              <div className="flex items-center gap-1">
                <Input
                  required
                  value={localPart}
                  onChange={(e) => setLocalPart(e.target.value.toLowerCase())}
                  placeholder="name"
                />
                <span className="text-ink-3">@</span>
                <Select
                  value={selectedDomain?.id ?? ""}
                  onChange={(e) => setDomainId(e.target.value)}
                >
                  {ready.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Select>
              </div>
            </Field>
          </div>
          <Button type="submit" disabled={create.isPending || withoutMailbox.length === 0}>
            {t("admin.mailboxes.create")}
          </Button>
        </form>
      )}
      <ErrorNotice error={create.error ?? reset.error ?? remove.error ?? mailboxes.error} />
      {secret ? (
        <div className="rounded-xl bg-good-soft p-4 text-sm">
          <p className="flex items-center gap-2 font-medium text-good">
            <KeyRound className="size-4" />
            {t("admin.mailboxes.secretTitle", { address: secret.address })}
          </p>
          <p className="mt-1 text-xs text-ink-2">{t("admin.mailboxes.secretHint")}</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="min-w-0 flex-1 rounded-lg bg-surface px-3 py-2 font-mono text-sm break-all">
              {secret.appPassword}
            </code>
            <button
              type="button"
              aria-label={t("common.copy")}
              title={t("common.copy")}
              className="flex size-9 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-surface"
              onClick={() => void navigator.clipboard.writeText(secret.appPassword)}
            >
              <Copy className="size-4" />
            </button>
          </div>
        </div>
      ) : null}
      <ul className="divide-y divide-rule">
        {(mailboxes.data?.mailboxes ?? []).map((m) => (
          <li key={m.id} className="flex items-start justify-between gap-3 py-3 text-sm">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
              <Mail className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-ink">{m.address}</div>
              <div className="text-xs text-ink-3">
                {m.displayName} · {Math.round(m.quotaBytes / 1024 / 1024 / 1024)} GB
              </div>
              <MailboxAliases mailbox={m} domains={ready} />
            </div>
            <div className="flex shrink-0 gap-2">
              <Button
                variant="secondary"
                aria-label={t("admin.mailboxes.newPassword")}
                title={t("admin.mailboxes.newPassword")}
                disabled={reset.isPending}
                className="px-3 sm:px-5"
                onClick={() => reset.mutate(m)}
              >
                <KeyRound className="size-4" />
                <span className="hidden sm:inline">{t("admin.mailboxes.newPassword")}</span>
              </Button>
              <button
                type="button"
                aria-label={t("admin.mailboxes.delete")}
                title={t("admin.mailboxes.delete")}
                disabled={remove.isPending}
                className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-danger-soft hover:text-danger"
                onClick={() => {
                  if (window.confirm(t("admin.mailboxes.confirmDelete", { address: m.address }))) {
                    remove.mutate(m.id);
                  }
                }}
              >
                <Trash2 className="size-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function MemberRow({
  user: u,
  isSelf,
  actorRole,
  open,
  onToggle,
  format,
}: {
  user: UserSummary;
  isSelf: boolean;
  actorRole: UserRole;
  open: boolean;
  onToggle: () => void;
  format: (iso: string) => string;
}) {
  const { t } = useTranslation();
  return (
    <>
      <tr className={u.suspended ? "opacity-60" : ""}>
        <td className="py-3 pr-3">
          <div className="flex items-center gap-3">
            <Avatar name={u.displayName || u.email} size={36} />
            <div className="min-w-0">
              <div className="truncate text-ink">
                {u.displayName}
                {isSelf ? (
                  <span className="ml-2 text-xs text-ink-3">({t("admin.users.actions.you")})</span>
                ) : null}
              </div>
              <div className="truncate text-xs text-ink-3">{u.email}</div>
              <div className="text-xs text-ink-2 sm:hidden">{t(`roles.${u.role}`)}</div>
            </div>
          </div>
        </td>
        <td className="hidden py-3 pr-3 text-ink-2 sm:table-cell">{t(`roles.${u.role}`)}</td>
        <td className="hidden py-3 pr-3 md:table-cell">
          <div className="flex flex-wrap gap-1">
            {u.suspended ? (
              <span className="rounded-full bg-danger-soft px-2 py-0.5 text-xs text-danger">
                {t("admin.users.actions.suspended")}
              </span>
            ) : null}
            {u.mfaEnabled ? (
              <span className="rounded-full bg-good-soft px-2 py-0.5 text-xs text-good">
                {t("admin.users.mfaOn")}
              </span>
            ) : (
              <span className="rounded-full bg-warn-soft px-2 py-0.5 text-xs text-warn">
                {t("admin.users.mfaOff")}
              </span>
            )}
            {u.emailVerified ? null : (
              <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs text-ink-2">
                {t("admin.users.unverified")}
              </span>
            )}
          </div>
        </td>
        <td className="hidden py-3 pr-3 text-ink-2 lg:table-cell">
          {u.lastLoginAt ? format(u.lastLoginAt) : "—"}
        </td>
        <td className="py-3 text-right">
          {isSelf ? null : (
            <Button variant="secondary" onClick={onToggle}>
              {open ? t("admin.users.actions.close") : t("admin.users.actions.manage")}
            </Button>
          )}
        </td>
      </tr>
      {open && !isSelf ? (
        <tr>
          <td colSpan={5} className="rounded-xl bg-surface-2 px-4 py-4">
            <MemberActions user={u} actorRole={actorRole} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/** The action panel of one member; every action refreshes the list and reports its outcome. */
function MemberActions({ user: u, actorRole }: { user: UserSummary; actorRole: UserRole }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [role, setRole] = useState<UserRole>(u.role);
  const [note, setNote] = useState<string | null>(null);
  const refresh = async (message: string) => {
    setNote(message);
    await queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
  };

  const run = useMutation({
    mutationFn: (action: () => Promise<unknown>) => action(),
    onSuccess: () => refresh(t("admin.users.actions.done")),
  });
  const isOwnerTarget = u.role === "owner";
  const canTransfer = actorRole === "owner" && !isOwnerTarget;
  const name = u.displayName;

  const saveRole = () => {
    if (role === "owner" && !window.confirm(t("admin.users.actions.confirmTransfer", { name }))) {
      return;
    }
    run.mutate(() => api.users.changeRole(u.id, role));
  };

  return (
    <div className="flex flex-col gap-3 text-sm">
      {isOwnerTarget ? null : (
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-56">
            <span className="mb-1 block text-xs font-medium text-ink-2">
              {t("admin.users.actions.role")}
            </span>
            <Select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
              {ASSIGNABLE_ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`roles.${r}`)}
                </option>
              ))}
              {canTransfer ? (
                <option value="owner">{t("admin.users.actions.transferOwnership")}</option>
              ) : null}
            </Select>
          </div>
          <Button onClick={saveRole} disabled={run.isPending || role === u.role}>
            {t("admin.users.actions.saveRole")}
          </Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {isOwnerTarget ? null : u.suspended ? (
          <Button
            variant="secondary"
            disabled={run.isPending}
            onClick={() => run.mutate(() => api.users.reactivate(u.id))}
          >
            {t("admin.users.actions.reactivate")}
          </Button>
        ) : (
          <Button
            variant="danger"
            disabled={run.isPending}
            onClick={() => {
              if (window.confirm(t("admin.users.actions.confirmSuspend", { name }))) {
                run.mutate(() => api.users.suspend(u.id));
              }
            }}
          >
            {t("admin.users.actions.suspend")}
          </Button>
        )}
        <Button
          variant="secondary"
          disabled={run.isPending}
          onClick={() => run.mutate(() => api.users.signOutEverywhere(u.id))}
        >
          {t("admin.users.actions.signOutEverywhere")}
        </Button>
        {isOwnerTarget ? null : (
          <Button
            variant="secondary"
            disabled={run.isPending}
            onClick={() => {
              if (window.confirm(t("admin.users.actions.confirmResetMfa", { name }))) {
                run.mutate(() => api.users.resetMfa(u.id));
              }
            }}
          >
            {t("admin.users.actions.resetMfa")}
          </Button>
        )}
        <Button
          variant="secondary"
          disabled={run.isPending}
          onClick={() => run.mutate(() => api.users.sendPasswordReset(u.id))}
        >
          {t("admin.users.actions.sendReset")}
        </Button>
      </div>
      <ErrorNotice error={run.error} />
      {note && !run.error ? <p className="text-xs text-good">{note}</p> : null}
    </div>
  );
}
